import LoaderModal from "components/common/Modal/LoaderModal";
import UserContext from "Context/UserProvider";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router";
import { isBusinessRole } from "utils/roleUtils";
import { 
    Box, 
    Typography, 
    TextField, 
    Button, 
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    IconButton,
    Stack,
    Autocomplete,
    Container,
    Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useApiService } from "../../services/ApiService/ApiService";
import toast from 'react-hot-toast';

function reducer(state: any, action: any) {
    switch (action.input) {
        case "email":
            return { ...state, email: action.value };
        case "firstName":
            return { ...state, firstName: action.value };
        case "lastName":
            return { ...state, lastName: action.value };
        case "password":
            return { ...state, password: action.value };
        case "businessOptions":
            return { ...state, businessOptions: action.value };
        case "userRoleOptions":
            return { ...state, userRoleOptions: action.value };
        default:
            return state;
    }
}

function AddUserPage(props: any) {
    const [options, setOptions] = useState([]) as any;
    const { userInfo } = useContext(UserContext);
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [isError, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("There was an error during creating user.");
    const [modalIsOpen, setIsOpen] = useState(false);
    const [userRoleOptions, setUserRoleOptions] = useState([]) as any;
    const [userRoleValue, setUserRoleValue] = useState(null) as any;
    const { fetchWithAuth } = useApiService();

    const initialState = {
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        businessOptions: [],
        userRoleOptions: [],
    };
    const [state, dispatch] = useReducer(reducer, initialState);

    const navigate = useNavigate();

    function handleClose() {
        setIsOpen(false);
        if (!isError) {
            navigate(-1);
        }
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        dispatch({
            input: e.target.name,
            value: e.target.value,
        });
    }

    function handleBusinessChange(event: any, newValue: any) {
        dispatch({
            input: 'businessOptions',
            value: newValue.map((item: any) => item.value)
        });
    }

    function handleRoleChange(event: any, newValue: any) {
        setUserRoleValue(newValue);
    }

    async function fetchBusinessInfo() {
        try {
            const response = await fetchWithAuth('/business/list', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    accountUuid: userInfo.accountUuid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch business info');
            }

            const res_json = await response.json();
            const options = res_json.businesses.map((e: any) => ({
                label: e.name,
                value: e.uuid,
            }));

            const userRoleOptions = res_json.roles.map((e: any) => ({
                label: e,
                value: e,
            }));

            setUserRoleOptions(userRoleOptions);
            setOptions(options);
            setError(false);
        } catch (error) {
            console.error('Error fetching business info:', error);
            setError(true);
            toast.error('Failed to load business information');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchBusinessInfo();
    }, []);

    const submit = async () => {
        setSubmitLoading(true);
        try {
            const bodyData = {
                uuid: userInfo.uuid,
                accountUuid: userInfo.accountUuid,
                businessUuids: isBusinessRole(userInfo.userType) ? [userInfo.businessUuid] : state.businessOptions,
                emailAddress: state.email,
                password: state.password,
                firstName: state.firstName,
                lastName: state.lastName,
                userRole: userRoleValue?.value
            };

            const response = await fetchWithAuth('/account/users/create', {
                method: 'POST',
                body: JSON.stringify(bodyData)
            });

            const res_json = await response.json();

            if (!response.ok) {
                switch (res_json.code) {
                    case "REASON_USER_ALREADY_EXISTS":
                        setErrorMessage("User already exists");
                        break;
                    case "REASON_PASSWORD_TOO_WEAK":
                        setErrorMessage("Your input password is too weak");
                        break;
                    case "BUSINESS_ROLES_CAN_ONLY_HAVE_ONE_BUSINESS":
                        setErrorMessage("Business user roles must be allocated to a single business");
                        break;
                    default:
                        setErrorMessage("Failed to create user");
                        break;
                }
                setError(true);
                setIsOpen(true);
                return;
            }

            dispatch({ input: "firstName", value: "" });
            dispatch({ input: "lastName", value: "" });
            dispatch({ input: "password", value: "" });
            dispatch({ input: "email", value: "" });
            setErrorMessage("User Created.");
            setError(false);
            setIsOpen(true);
        } catch (error) {
            console.error('Error creating user:', error);
            setErrorMessage("Failed to create user");
            setError(true);
            setIsOpen(true);
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container 
            maxWidth={false}
            sx={{ 
                py: 3,
                width: {
                    xs: '100%',    // Full width on mobile
                    sm: '100%',    // Full width on tablet
                    md: '66.67%',  // 2/3 width on medium screens
                    lg: '33.33%',  // 1/3 width on large screens
                },
                mx: 'auto'
            }}
        >
            <Paper 
                elevation={0} 
                sx={{ 
                    borderRadius: 1,
                    overflow: 'hidden',
                    p: 3
                }}
            >
                <Stack spacing={3}>
                    <TextField
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={state.firstName}
                        onChange={handleInputChange}
                    />
                    <TextField
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={state.lastName}
                        onChange={handleInputChange}
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={state.email}
                        onChange={handleInputChange}
                    />
                    <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type="password"
                        value={state.password}
                        onChange={handleInputChange}
                    />
                    {!isBusinessRole(userInfo.userType) && (
                        <Autocomplete
                            multiple
                            options={options}
                            getOptionLabel={(option) => option.label}
                            onChange={handleBusinessChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Businesses"
                                    placeholder="Businesses"
                                />
                            )}
                        />
                    )}
                    <Autocomplete
                        options={userRoleOptions}
                        getOptionLabel={(option) => option.label}
                        onChange={handleRoleChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Role"
                                placeholder="Role"
                            />
                        )}
                    />
                    <Button
                        variant="contained"
                        onClick={submit}
                        disabled={submitLoading}
                        fullWidth
                    >
                        {submitLoading ? <CircularProgress size={24} /> : 'Create User'}
                    </Button>
                </Stack>
            </Paper>

            <Dialog
                open={modalIsOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            {isError ? 'Error' : 'Success'}
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity={isError ? "error" : "success"}>
                        {errorMessage}
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        {isError ? 'Close' : 'Done'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default AddUserPage;
